<template>
  <v-container>
    <div class="questionnaires-edit">
      <v-row class="mt-2 d-flex justify-start">
        <template v-if="published">
          <div class="d-inline">
            <v-chip
              class="ma-2"
              color="orange"
              size="small"
              text-color="white"
            >
              <v-icon
                left
                icon="mdi-eye"
              />
              公開中
            </v-chip>
            <v-btn
              size="x-small"
              variant="outlined"
              rounded="pill"
              @click="cancel"
            >
              非公開に戻す
            </v-btn>
          </div>
        </template>
        <template v-else>
          <div class="d-inline">
            <v-chip
              class="ma-2"
              color="light-green"
              size="small"
              text-color="white"
            >
              <v-icon
                left
                icon="mdi-eye-off-outline"
              />
              非公開
            </v-chip>
            <v-btn
              size="x-small"
              variant="outlined"
              rounded="pill"
              @click="fixed"
            >
              公開する
            </v-btn>
          </div>
        </template>
      </v-row>

      <v-row class="my-2">
        <v-col>
          <h2 class="text-no-wrap">
            オンライン相談：アンケートフリー回答一覧
          </h2>
        </v-col>
        <v-col class="pl-0 d-flex justify-end align-center">
          <div class="picker-align-right">
            <month-picker
              v-model:selectedDate="selectedDate"
              @on-month-changed="saveSelectedDate"
            />
          </div>
          <v-btn
            v-if="published"
            variant="flat"
            prepend-icon="custom:download"
            height="40"
            border="md"
            @click="downloadCSV"
            class="text-glance-primary ml-2 font-weight-bold border-current"
          >
            CSVダウンロード
          </v-btn>
        </v-col>
      </v-row>

      <v-table
        v-if="afterUseQuestionnaires.length > 0"
        class="editable-table table-grid font-size-10-rem"
      >
        <thead class="height-40">
          <tr>
            <th
              class="w-15 text-center font-weight-bold"
            >
              相談相手
            </th>
            <th
              class="w-20 text-center font-weight-bold"
            >
              サービス種別
            </th>
            <th
              class="text-center font-weight-bold"
            >
              利用後アンケートフリー回答
            </th>
            <th
              class="w-30 text-center font-weight-bold"
            >
              再利用意向
            </th>
          </tr>
        </thead>
        <tbody class="height-45">
          <tr
            v-for="questionnaire in afterUseQuestionnaires"
            :key="questionnaire.id"
            :class="[isSyounika(questionnaire.departmentType) ? 'bg-syounika' : 'bg-obstetrics']"
          >
            <td class="text-center">
              {{ questionnaire.departmentTypeI18n }}
            </td>
            <td class="text-center">
              {{ questionnaire.reservationTypeI18n }}
            </td>
            <td>
              <template v-if="editable">
                <v-textarea
                  v-model="questionnaire.requestOrOpinion"
                  class="my-1"
                  rows="2"
                  clearable
                  dense
                  hide-details
                />
              </template>
              <template v-else>
                <!-- eslint-disable-next-line vue/no-v-html -->
                <span v-html="newLineCodeToBr(questionnaire.requestOrOpinion)" />
              </template>
            </td>
            <td class="text-center">
              {{ questionnaire.howLikelyUseServiceAgain }}
            </td>
          </tr>
        </tbody>
      </v-table>
      <v-row class="my-3 d-flex justify-center">
        <v-btn
          v-if="!published && !editable"
          class="px-8 text-white font-weight-bold"
          :color="color.mainColor()"
          @click="edit"
        >
          編集
        </v-btn>
        <v-btn
          v-if="!published && editable"
          class="mr-2 px-8"
          :color="color.mainColor()"
          variant="outlined"
          @click="reset"
        >
          キャンセル
        </v-btn>
        <v-btn
          v-if="!published && editable"
          class="ml-2 px-8 text-white font-weight-bold"
          :color="color.mainColor()"
          @click="save"
        >
          保存
        </v-btn>
      </v-row>
      <confirm-dialog
        :show-dialog="showDialog"
        :dialog-title="dialogTitle"
        :dialog-message="dialogMessage"
        @on-click-dialog-cancel="closeDialog"
        @on-click-dialog-ok="published ? commitCancel() : commitFixed()"
      />
    </div>
  </v-container>
</template>

<script lang="ts">
import { defineComponent, computed, onMounted, ref } from "vue"
import { useStore } from "../stores/index"
import { AfterUseQuestionnaire } from "../stores/store_types"
import { dateFormatter } from "../service/date_formatter"
import { textFormatter } from "../service/text_formatter"
import { warningMessage } from "../service/warning_message"
import { dateUtility } from "../service/date_utility"
import monthPicker from "./shared/MonthPicker.vue"
import confirmDialog from "./shared/ConfirmDialog.vue"
import { themeColor } from "../service/theme_color"

export default defineComponent({
  name: "QuestionnairesEditPage",
  components: {
    monthPicker,
    confirmDialog
  },
  setup() {
    // vuex
    const store = useStore()
    // settings
    const color = themeColor()

    // data
    const corporation = computed(() => store.getters.corporation)
    const afterUseQuestionnaires = computed(() => store.getters.afterUseQuestionnaires)
    const published = computed(() => store.getters.afterUseQuestionnairesPublished)
    const afterUseQuestionnairePublicAvailability = computed(() => store.getters.afterUseQuestionnairePublicAvailability)
    const selectedDate = computed(() => new Date(store.getters.selectedQuestionnaireDate))
    const editable = ref<boolean>(false)
    const dateFormat = dateFormatter()
    const newLineCodeToBr = textFormatter().newLineCodeToBr
    const showDialog = ref<boolean>(false)
    const dialogTitle = ref<string>("")
    const dialogMessage = ref<string>("")

    const isSyounika = (departmentType: string) => {
      if (departmentType == "pediatrician") {
        return true
      }

      return false
    }

    const saveSelectedDate = async (selectedValue: Date) => {
      // 編集モードを解除する
      editable.value = false
      await store.dispatch("commitSelectedQuestionnaireDate", selectedValue)
    }

    const fixed = () => {
      dialogTitle.value = "利用後アンケートの公開"
      let message = `【${corporation.value.name}】様の【${dateFormat.formatYearMonthJp(selectedDate.value)}】利用後アンケートを公開します。よろしいですか？`
      if( dateUtility().isCurrentMonthSelected(selectedDate.value) ){
        message = message + warningMessage
      }
      dialogMessage.value = message
      openDialog()
    }

    const commitFixed = async () => {
      await store.dispatch("createAfterUseQuestionnairePublicAvailability")
      closeDialog()
    }

    const cancel = () => {
      dialogTitle.value = "利用後アンケートの非公開"
      let message = `【${corporation.value.name}】様の【${dateFormat.formatYearMonthJp(selectedDate.value)}】利用後アンケートを非公開にします。よろしいですか？`
      dialogMessage.value = message
      openDialog()
    }

    const commitCancel = async () => {
      await store.dispatch("deleteAfterUseQuestionnairePublicAvailability", afterUseQuestionnairePublicAvailability.value.id)
      closeDialog()
    }

    const edit = async () => {
      // 再読み込み
      await store.dispatch("fetchAfterUseQuestionnaires").then(() => {
        editable.value = true
      })
    }

    const save = async () => {
      const params: {id: number, requestOrOpinion: string}[] = []
      afterUseQuestionnaires.value.forEach((auq: AfterUseQuestionnaire) => {
        params.push({
          id: auq.id,
          requestOrOpinion: auq.requestOrOpinion
        })
      })
      await store.dispatch("updateAfterUseQuestionnaires", params).then(() => {
        editable.value = false
      })
    }

    const reset = async () => {
      // 再読み込み
      await store.dispatch("fetchAfterUseQuestionnaires").then(() => {
        editable.value = false
      })
    }

    const downloadCSV = async () => {
      await store.dispatch("downloadAfterUseQuestionnaires")
    }

    const openDialog = () => {
      showDialog.value = true
    }

    const closeDialog = () => {
      showDialog.value = false
    }

    onMounted(() => {
      store.dispatch("commitSelectedQuestionnaireDate", selectedDate.value)
    })

    return {
      selectedDate,
      afterUseQuestionnaires,
      published,
      editable,
      saveSelectedDate,
      fixed,
      cancel,
      edit,
      save,
      reset,
      downloadCSV,
      newLineCodeToBr,
      isSyounika,
      showDialog,
      dialogTitle,
      dialogMessage,
      commitFixed,
      commitCancel,
      closeDialog,
      color
    }
  }
})
</script>
